import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import PolicyDetailsForm from './PolicyDetailsForm';
import ApplicationDetailsForm from './ApplicationDetailsForm';
import CustomerPolicyAnswersPage from '../admin/customers/CustomerPolicyAnswersPage';
import RejectPolicyModal from './RejectPolicyModal';

import { Button } from '../components/buttons';
import { IconAdd, IconRemove } from '../components/icons';
import { Spinner } from '../components/common';
import { FormCheckConditional } from '../components/forms';
import * as toastr from 'toastr';

import Utils, { SecurityRoles, PolicyStatus, AdjudicationStatus } from '../common/utils';

import userActions from '../user/userActions';
import policyActions from './policyActions';
import paymentActions from '../payment/paymentActions';

interface Props {
	className?: string;
	customer: UserDetails;
	customerPolicy: CustomerPolicy;
	policies: CustomerPolicy[];
	answers: PolicyAnswerWithDeclineInfoDto;
	notes: CustomerNote[];
	questionnaireReport: any;
	currentUser: UserDetails;
	isHardDeclined: DeclineCheckerDto;
	redirectFromAdjudication: boolean;
	actions: any;
	history: any;
	match: any;
}
interface State {
	isLoading: boolean;
	overwriteRate: boolean;
	answerExpanded: boolean;
	isRejectPolicyModalOpen: boolean;
}
class PolicyDetails extends React.Component<Props, State> {
	state = {
		isLoading: true,
		overwriteRate: false,
		answerExpanded: false,
		isRejectPolicyModalOpen: false
	};

	componentDidMount() {
		this.loadData();
	}

	loadData = () => {
		const p1 = this.props.actions.fetchCustomerPolicy(this.props.match.params.customerId, this.props.match.params.pageId);
		const p2 = this.props.actions.fetchPolicyAnswer(this.props.match.params.pageId);
		const p3 = this.props.actions.fetchIsHardDeclined(this.props.match.params.customerId, this.props.match.params.pageId);
		Promise.all([p1, p2, p3])
			.then(() => this.setState({ isLoading: false }));
	}

	onPolicyChange = (e: any) => {
		if (e.target.type === 'text' && !e.target.validity.valid) {
			return;
		}

		const customerPolicy = {
			...this.props.customerPolicy,
			[e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}

	onOverwirteChange = (e: any) => {
		if (e.target.checked === false) {
			const policyBeforeEdit = this.props.policies.find(p => p.id === this.props.match.params.pageId);
			const customerPolicy = {
				...this.props.customerPolicy,
				rate: policyBeforeEdit!.rate
			};
	
			this.props.actions.setCustomerPolicy(customerPolicy);
		}
		this.setState({overwriteRate: e.target.checked});
	}

	answerExpand = () => {
		this.setState({answerExpanded: !this.state.answerExpanded});
	}

	downloadQuestionnaireReport = () => {
		this.setState({ isLoading: true });
		this.props.actions.fetchQuestionnaireReport(this.props.customer.id, this.props.customerPolicy.id)
			.then(() => {
				let filename = this.props.customerPolicy.statusId === PolicyStatus.Inactive ? 'Application' : 'Policy';
				filename += `${this.props.customerPolicy.number}-QuestionnaireReport.csv`;
				Utils.downloadCsv(this.props.questionnaireReport, filename, true);
				this.setState({ isLoading: false });
			});
	}
	canEdit = () => {
		return Utils.isAuthorized(this.props.currentUser.role!, [SecurityRoles.Administrator]) &&
		this.props.customerPolicy.statusId === PolicyStatus.Active;
	}

	canAdjudicate = () => {
		return this.props.customerPolicy.isSoftDeclined &&
		this.props.customerPolicy.adjudicationStatusId !== AdjudicationStatus.Rejected;
	}

	back = () => {
		if (!window.location.href.includes('prospects')) {
			return this.props.history.push(`/admin/customers/${this.props.match.params.customerId}/policies`);
		} else {
			return this.props.history.push(`/admin/prospects/${this.props.match.params.customerId}/policies`);
		}
	}

	markAsInProcess = () => {
		let policy = {...this.props.customerPolicy};
		policy.adjudicationStatusId = AdjudicationStatus.InProcess;
		this.props.actions.saveCustomerPolicy(policy)
		.then(() => {
			toastr.success('Mark customer as in process');
		});
	}

	openRejectPolicyModal = () => {
		this.setState({ isRejectPolicyModalOpen: true });
	}

	closeRejectPolicyModal = () => {
		this.setState({ isRejectPolicyModalOpen: false });
	}

	markAsDeclined = (sendNotification: boolean) => {
		let policy = {...this.props.customerPolicy};
		policy.adjudicationStatusId = AdjudicationStatus.Rejected;
		this.props.actions.saveCustomerPolicy(policy)
		.then(() => {
			if (sendNotification) {
				return this.props.actions.sendSoftDeclinedEmail(this.props.customer.id);
			} else {
				return Promise.resolve();
			}
		})
		.then(() => {
			toastr.success('Application has been declined');
			if (this.props.redirectFromAdjudication) {
				this.props.history.push(`/admin/adjudication`);
			} else {
				this.props.history.push(`/admin/customers`);
			}
		});
	}

	saveAndUpdatePaymentInfo = () => {
		if (this.canAdjudicate()) {
			this.setState({ isLoading: true });
			this.props.actions.updateCustomerPlan(this.props.customerPolicy, this.state.overwriteRate)
			.then(() => {
				this.setState({ isLoading: false });
				this.props.history.push(`/admin/prospects/${this.props.match.params.customerId}/` +
				`policies/${this.props.match.params.pageId}/payment-update`);
			});
		}
	}

	save = () => {
		if (this.canEdit()) {
			this.updateCustomerPlan();
		}
	}

	updateCustomerPlan = () => {
		this.setState({ isLoading: true });
		this.props.actions.updateCustomerPlan(this.props.customerPolicy, this.state.overwriteRate).then(() => {
			this.setState({ isLoading: false });
			toastr.success('Policy updated!');
			if (!window.location.href.includes('prospects')) {
				return this.props.history.push(`/admin/customers/${this.props.match.params.customerId}/policies`);
			} else {
				return this.props.history.push(`/admin/prospects/${this.props.match.params.customerId}/policies`);
			}
		});
	}

	cancelCustomerPlan = () => {
		if (window.confirm('Are you sure you want to cancel this policy?')) {
			this.setState({ isLoading: true });
			this.props.actions.deleteCustomerPolicy(this.props.customer, this.props.customerPolicy)
				.then(() => {
					this.props.actions.sendCancelPolicyEmail(this.props.customer.id, this.props.customerPolicy.id);
				})
				.then(() => {
					this.setState({ isLoading: false });
					toastr.success('Policy canceled!');
					this.props.history.push(`/admin/customers/${this.props.match.params.customerId}/policies`);
				});
		}
	}

	allDeclinedAnswerHasNotes = () => {
		const declinedAnswers = this.props.answers.answerList!.filter(a => a.isDeclined);
		let count = 0;
		declinedAnswers.forEach(d => {
			if (this.props.notes.some(n => n.answerId === d.id)) {
				count++;
			}
		});
		return count === declinedAnswers.length;
	}

	updatePolicyPaperCopyStatus = (policyId: string) => {
		this.setState({ isLoading: true });
		this.props.actions.updatePolicyPaperCopyStatus(policyId)
		.then(() => this.loadData())
		.then(() => this.setState({ isLoading: false }));
	}

	render() {
		const allowFormat = this.props.customerPolicy.statusId ? true : false;
		return (
			<div className={this.props.className}>
				{this.props.customerPolicy.statusId === PolicyStatus.Inactive ?
					<ApplicationDetailsForm
						application={this.props.customerPolicy}
						onPolicyChange={this.onPolicyChange}
						onOverwirteChange={this.onOverwirteChange}
						readOnly={!this.canAdjudicate()}
						isLoading={this.state.isLoading}
						overwriteRate={this.state.overwriteRate}
						isAdjudicator={Utils.isAuthorized(this.props.currentUser.role!, [SecurityRoles.Adjudicator])}
					/>
					:
					<PolicyDetailsForm
						customerPolicy={this.props.customerPolicy}
						onPolicyChange={this.onPolicyChange}
						onOverwriteChange={this.onOverwirteChange}
						downloadQuestionnaireReport={this.downloadQuestionnaireReport}
						readOnly={!this.canEdit()}
						isLoading={this.state.isLoading}
						overwriteRate={this.state.overwriteRate}
						updatePolicyPaperCopyStatus={this.updatePolicyPaperCopyStatus}
					/>
				}
				<h2 className="form-fieldset-title mt-4 mb-0 border-0">
					<button
						className={`faq-question-clickable${this.state.answerExpanded ? ' active' : ''}`}
						onClick={this.answerExpand}
					>
						{this.state.answerExpanded ? <IconRemove /> : <IconAdd />}
						<span className="faq-question-title">Policy Questionnaire Answers</span>
					</button>
				</h2>
				<FormCheckConditional className="faq-question-answer" isOpen={this.state.answerExpanded}>
					<CustomerPolicyAnswersPage
							history={this.props.history}
							match={this.props.match}
							answers={this.props.answers}
							allowFormat={allowFormat}
					/>
				</FormCheckConditional>
				{this.props.customerPolicy.statusId === PolicyStatus.Inactive ?
					(this.showAdjudicationControls(this.props.customerPolicy) &&
						<div className="table-options mt-4">
							{this.props.customerPolicy.adjudicationStatusId &&
							<Button
								onClick={this.markAsInProcess}
								className="btn-primary"
								disabled={this.state.isLoading ||
								this.props.customerPolicy.adjudicationStatusId === AdjudicationStatus.InProcess ||
								this.props.customerPolicy.adjudicationStatusId === AdjudicationStatus.Rejected}
							>
								Set to 'In Process'
								{this.state.isLoading && <Spinner/>}
							</Button>}
							<div className="ml-auto">
							<Button
								onClick={this.saveAndUpdatePaymentInfo}
								className="btn-secondary mr-2"
								disabled={this.state.isLoading || !this.allDeclinedAnswerHasNotes()}
							>
								Activate Policy
								{this.state.isLoading && <Spinner/>}
							</Button>
							<Button onClick={this.openRejectPolicyModal} className="btn-primary" disabled={this.state.isLoading}>
								Reject Policy
								{this.state.isLoading && <Spinner/>}
							</Button>
							</div>
						</div>
					) :
					(<div className="table-options mt-4">
						<div className="btn-group table-filters">
							<div className="table-filter">
								<Button onClick={this.save} className="btn-primary" disabled={!this.canEdit()}>Save</Button>
							</div>
						</div>
						<Button
							className="m-0 btn-outline-primary"
							onClick={this.cancelCustomerPlan}
							disabled={this.props.customerPolicy.statusId === PolicyStatus.Canceled}
						>
							Cancel policy
						</Button>
					</div>)
				}
				<RejectPolicyModal
					isOpen={this.state.isRejectPolicyModalOpen}
					onRequestClose={this.closeRejectPolicyModal}
					markAsDeclined={this.markAsDeclined}
				/>
			</div>
		);
	}
	showAdjudicationControls(customerPolicy: CustomerPolicy): any {
		return (!this.props.isHardDeclined.isDeclined || this.state.overwriteRate)
			&& customerPolicy.adjudicationStatusId !== AdjudicationStatus.Rejected;
	}
}

function mapStateToProps(state: any) {
	return {
		customer: state.user.user,
		customerPolicy: state.policy.customerPolicy,
		questionnaireReport: state.policy.questionnaireReport,
		currentUser: state.auth.currentUser,
		answers: state.policy.answers,
		notes: state.user.notes,
		policies: state.policy.policies,
		isHardDeclined: state.policy.isHardDeclined,
		redirectFromAdjudication: state.policy.redirectFromAdjudication
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions, ...paymentActions, ...userActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default connect(mapStateToProps, mapDispatchToProps)(PolicyDetails);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/PolicyDetails.tsx